import { isEmpty } from '../../core/util';
import { minimumSummitIClassInvestAmount } from './summit.utils';
import { addCommas, I_CLASS_MINIMUM } from './utils';

/** * REGEX ** */
/* eslint-disable prefer-regex-literals */
export const ADDRESS_REGEX = new RegExp(/^[A-Za-z0-9-_.,':#&/\s]+$/);

export const PO_BOX_ADDRESS1_REGEX = new RegExp(
  /^((#\d+)|((box|bin)[-. /\\]?\d+)|(.*p[ .]? ?(o|0)[-. /\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|post.office.box|((box|bin)|b) *(number|num|#)? *\d+|(num|number|#) *\d+)/i
);

export const PO_BOX_ADDRESS2_REGEX = new RegExp(
  /^(((box|bin)[-. /\\]?\d+)|(.*p[ .]? ?(o|0)[-. /\\]? *-?((box|bin)|b|(num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|post.office.box|((box|bin)|b) *(number|num)? *\d+|(num|number) *\d+)/i
);

export const ZIPCODE_REGEX = new RegExp(/^[0-9]{5}$/);

export const ROUTINGNUMBER_REGEX = new RegExp(/^[0-9]{9}$|^[0-9]{9}-[0-9]{8}$/);

export const ACCOUNTNAME_REGEX = new RegExp(/^([ A-Za-z0-9-_.,'&%@+()]*)?$/);

export const CITY_REGEX = new RegExp(
  /^[ A-Za-z0-9][ A-Za-z0-9-_.,'&@]*[ A-Za-z0-9-_.,'&@]$/
);
export const STATE_REGEX = new RegExp(/[A-Z]/);

export const NAME_REGEX = new RegExp(/^[A-Za-z]+[ A-Za-z-_.,'&]*$/);

export const RESTRICTED_CHAR_NAME_REGEX = new RegExp(
  /^[A-Za-z]+[ A-Za-z-'&]*$/
);

export const TRUST_NAME_REGEX = new RegExp(/^[A-Za-z0-9]+[ A-Za-z0-9-_.,'&]*$/);

export const PHONE_REGEX = new RegExp(
  /^(\+0?1\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/
);
export const EMAIL_REGEX = new RegExp(
  /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9._%-]+\.[a-zA-Z]{2,6}$/
);

export const SSN_REGEX = new RegExp(/\d{3}[-]\d{2}[-]\d{4}/);
export const EIN_REGEX = new RegExp(/\d{2}[-]\d{7}/);

/** * ERROR MESSAGES ** */
export const FIRST_NAME_ERROR_MSG = 'Invalid Character in First Name';

export const MIDDLE_NAME_ERROR_MSG = 'Invalid Character in Middle Name';

export const LAST_NAME_ERROR_MSG = 'Invalid Character in Last Name';

export const TRUST_NAME_ERROR_MSG = 'Invalid Character in Trust Name';

export const ADDRESS_LINE1_ERROR_MSG =
  'Invalid Character in Residential / Legal Address Line 1';

export const ADDRESS_LINE2_ERROR_MSG =
  'Invalid Character in Residential / Legal Address Line 2';

export const PO_BOX_ERROR_MSG =
  'Invalid Residential / Legal Address - Residential / Legal address must not contain P.O. Box';

export const ZIPCODE_ERROR_MSG = 'Invalid zipcode format';

export const ROUTING_NUMBER_ERROR_MSG = 'Invalid routing number format';
export const ACCOUNT_NAME_ERROR_MSG = 'Invalid account name';

export const EMAIL_ERROR_MSG = 'This is not a valid email. Please try again';

export const PHONE_ERROR_MSG = 'Invalid phone number';
export const AT_LEAST_ONE_PHONE_NUMBER = 'Enter at least one phone number';

export const CITY_ERROR_MSG = 'Invalid city format';

export const STATE_ERROR_MSG = 'State must be uppercase';

export const REQUIRED_ERROR_MSG = 'Required';

export const MILITARY_STATES = ['AA', 'AE', 'AP'];
export const MILITARY_CITIES = ['APO', 'DPO', 'FPO'];

export const INVALID_TAXID = "You've entered an incorrect pattern for tax ID";

export const USERNAME_ERROR_MSG = 'Invalid username';
export const PASSWORD_ERROR_MSG = 'Invalid password';

/** * FUNCTIONS ** */
export const valueRequired = (value: any): string | boolean =>
  value === '' && REQUIRED_ERROR_MSG;

export const firstnameValidation = (value: any): string | boolean =>
  value && !NAME_REGEX.test(value) && FIRST_NAME_ERROR_MSG;

export const restrictedFirstNameValidation = (value: any): string | boolean =>
  value && !RESTRICTED_CHAR_NAME_REGEX.test(value) && FIRST_NAME_ERROR_MSG;

export const lastnameValidation = (value: any): string | boolean =>
  value && !NAME_REGEX.test(value) && LAST_NAME_ERROR_MSG;

export const restrictedLastNameValidation = (value: any): string | boolean =>
  value && !RESTRICTED_CHAR_NAME_REGEX.test(value) && LAST_NAME_ERROR_MSG;

export const middlenameValidation = (value: any): string | boolean =>
  value && !NAME_REGEX.test(value) && MIDDLE_NAME_ERROR_MSG;

export const restrictedMiddleNameValidation = (value: any): string | boolean =>
  value && !RESTRICTED_CHAR_NAME_REGEX.test(value) && MIDDLE_NAME_ERROR_MSG;

export const trustnameValidation = (value: any): string | boolean =>
  value && !TRUST_NAME_REGEX.test(value) && TRUST_NAME_ERROR_MSG;

export const addressLine1Validation = (value: any): string | boolean =>
  value && !ADDRESS_REGEX.test(value) && ADDRESS_LINE1_ERROR_MSG;

export const addressLine2Validation = (value: any): string | boolean =>
  value && !ADDRESS_REGEX.test(value) && ADDRESS_LINE2_ERROR_MSG;

export const poBoxLine1Validation = (value: any): string | boolean =>
  value && PO_BOX_ADDRESS1_REGEX.test(value.trim()) && PO_BOX_ERROR_MSG;

export const poBoxLine2Validation = (value: any): string | boolean =>
  value && PO_BOX_ADDRESS2_REGEX.test(value.trim()) && PO_BOX_ERROR_MSG;

export const shouldBeMilitaryCity = (
  city: string,
  state: string
): string | boolean => {
  const curCity = city?.toUpperCase();
  return (
    state &&
    MILITARY_STATES.includes(state) &&
    !MILITARY_CITIES.includes(curCity)
  );
};

export const shouldBeMilitaryState = (
  city: string,
  state: string
): string | boolean => {
  const curCity = city?.toUpperCase();
  return (
    state &&
    MILITARY_CITIES.includes(curCity) &&
    !MILITARY_STATES.includes(state)
  );
};

export const cityValidation = (
  city: string,
  otherValues: any
): string | boolean => {
  if (shouldBeMilitaryCity(city, otherValues?.residentialState)) {
    return `Based on your state of ${
      otherValues?.residentialState
    } your chosen city must be ${MILITARY_CITIES.join(', ')}`;
  }

  return city && !CITY_REGEX.test(city) && CITY_ERROR_MSG;
};

export const stateValidation = (
  state: string,
  otherValues: any
): string | boolean => {
  if (shouldBeMilitaryState(otherValues?.residentialCity, state)) {
    return `Based on your city of ${
      otherValues?.residentialCity
    } your chosen state must be ${MILITARY_STATES.join(', ')}`;
  }

  return state && !STATE_REGEX.test(state) && STATE_ERROR_MSG;
};

export const mailingCityValidation = (
  city: string,
  otherValues: any
): string | boolean => {
  if (shouldBeMilitaryCity(city, otherValues?.mailingState)) {
    return `Based on your state of ${
      otherValues?.mailingState
    } your chosen city must be ${MILITARY_CITIES.join(', ')}`;
  }

  return city && !CITY_REGEX.test(city) && CITY_ERROR_MSG;
};

export const mailingStateValidation = (
  state: string,
  otherValues: any
): string | boolean => {
  if (shouldBeMilitaryState(otherValues?.mailingCity, state)) {
    return `Based on your city of ${
      otherValues?.mailingCity
    } your chosen state must be ${MILITARY_STATES.join(', ')}`;
  }

  return state && !STATE_REGEX.test(state) && STATE_ERROR_MSG;
};

export const zipCodeValidation = (zipCodeValue: any): string | boolean =>
  zipCodeValue && !ZIPCODE_REGEX.test(zipCodeValue) && ZIPCODE_ERROR_MSG;

export const emailValidation = (emailValue: string): string | boolean =>
  emailValue && !EMAIL_REGEX.test(emailValue) && EMAIL_ERROR_MSG;

export const phoneValidation = (
  phoneNumberValue = '',
  otherValues: any = {}
): string | boolean => {
  if (phoneNumberValue.trim() === '') {
    return (
      isEmpty(otherValues.phone2) &&
      isEmpty(otherValues.phone3) &&
      AT_LEAST_ONE_PHONE_NUMBER
    );
  }

  return !PHONE_REGEX.test(phoneNumberValue) && PHONE_ERROR_MSG;
};

export const normalizeSSN = (val): string =>
  val
    ?.replace(/\D/g, '')
    .substr(0, 9)
    .replace(
      /(\d{1,3})(\d{1,2})?(\d{1,4})?/g,
      (txt, first3, middle2, trailing4): string => {
        if (trailing4) {
          return `${first3}-${middle2}-${trailing4}`;
        }
        if (middle2) {
          return `${first3}-${middle2}`;
        }
        if (first3) {
          return `${first3}`;
        }
        return '';
      }
    );

export const normalizeEIN = (val): string =>
  val
    ?.replace(/\D/g, '')
    .substr(0, 9)
    .replace(/(\d{1,2})?(\d{1,7})?/g, (txt, first2, trailing7): string => {
      if (trailing7) {
        return `${first2}-${trailing7}`;
      }
      if (first2) {
        return `${first2}`;
      }
      return '';
    });

export const routingNumberValidation = (
  routingNumberValue: any
): string | boolean =>
  routingNumberValue &&
  !ROUTINGNUMBER_REGEX.test(routingNumberValue) &&
  ROUTING_NUMBER_ERROR_MSG;

export const accountNameValidation = (
  accountNameValue: any
): string | boolean =>
  accountNameValue &&
  !ACCOUNTNAME_REGEX.test(accountNameValue) &&
  ACCOUNT_NAME_ERROR_MSG;

export const validateTransferAmount = ({
  chosenInvestments,
  summitTier = 0
}): any => {
  // check iclass fund is present; bail out if not
  const hasIClassInvestments = chosenInvestments?.some(ci => ci?.iClass);
  if (!hasIClassInvestments) return '';

  // query all amount fields present
  const transferAmountFields = document.querySelectorAll(
    'input[type="number"]'
  );
  // capture all amount fields values
  const transferAmounts = Object.values(transferAmountFields)?.map(
    (ele: HTMLInputElement) => ele?.value
  );

  // bail out if no amount values are present
  if (transferAmounts.length === 0) return '';

  // total number of iclass funds to be invested in
  const numberOfIClassFunds = chosenInvestments.filter(i => i.iClass)?.length;
  // parse values to numbers and total
  // account for empty string values and default to zero
  const sum = transferAmounts
    .map(val => (!isEmpty(val) ? parseInt(val, 10) : 0))
    .reduce((acc, curr) => acc + curr);

  // check summit rewards requirements for iclass
  if (summitTier >= 4) {
    // determine total amount based on number of iclass funds times base minimum requirement of 50K
    const minimumTotalRequired =
      numberOfIClassFunds * minimumSummitIClassInvestAmount;
    return sum < minimumTotalRequired
      ? `Transfer amount must be greater than or equal to $${addCommas(
          minimumTotalRequired
        )}`
      : '';
  }
  // check non-reward iclass requirements
  // determine total amount based on number of iclass funds times base minimum requirement of 500K
  const minimumTotalRequired = numberOfIClassFunds * I_CLASS_MINIMUM;
  return sum < minimumTotalRequired
    ? `Transfer amount must be greater than or equal to $${addCommas(
        minimumTotalRequired
      )}`
    : '';
};
